import type { StaffLocale } from '@rental/domain'
import { format, formatDate, formatEuro, plural, type StaffMessages } from '@rental/i18n'

type Words = StaffMessages['prices']

/** What a day holds (the API's StaffPriceValues): null is "nothing set". */
export interface DayValues {
  priceCents: number | null
  minDaysPickup: number | null
  minDaysThrough: number | null
  stopSale: boolean
}

/** A day with no cell: nothing set. */
export const NOTHING_SET: DayValues = {
  priceCents: null,
  minDaysPickup: null,
  minDaysThrough: null,
  stopSale: false,
}

/** How many more one-day rentals fit, of how many vehicles; free is null when not known. */
export interface Occupancy {
  free: number | null
  total: number
}

/**
 * The free vehicles as the cell shows them and says them. Never a booked
 * count: only what is free, of the total. Not known, it says why: the
 * rental day's start time is not set, or the count did not finish.
 */
export function freeWords(
  words: Words,
  occupancy: Occupancy,
  boundarySet: boolean,
): { shown: string; note: string | null; said: string } {
  const { free, total } = occupancy
  if (free !== null) {
    return {
      shown: format(words.free, { free, total }),
      note: null,
      said: format(words.freeSaid, { free, total }),
    }
  }
  return {
    shown: format(words.freeUnknown, { total }),
    note: boundarySet ? words.notCounted : words.noBoundary,
    said: format(boundarySet ? words.notCountedSaid : words.noBoundarySaid, { total }),
  }
}

/** The price as shown: euros, or a dash for none. */
export function priceShown(locale: StaffLocale, words: Words, cents: number | null): string {
  return cents === null ? words.noPrice : formatEuro(cents, locale)
}

/** A day's values in words, for a sentence: the price, the minimums set, and stop sale. */
export function valuesSaid(locale: StaffLocale, words: Words, values: DayValues): string[] {
  return [
    values.priceCents === null ? words.noPriceSaid : formatEuro(values.priceCents, locale),
    ...(values.minDaysPickup === null
      ? []
      : [plural(locale, words.minPickupSaid, values.minDaysPickup)]),
    ...(values.minDaysThrough === null
      ? []
      : [plural(locale, words.minThroughSaid, values.minDaysThrough)]),
    ...(values.stopSale ? [words.stopSale] : []),
  ]
}

/** A day's values in short, as the cell shows them: for a preview's before and after. */
export function valuesShort(locale: StaffLocale, words: Words, values: DayValues): string {
  return [
    priceShown(locale, words, values.priceCents),
    ...(values.minDaysPickup === null
      ? []
      : [format(words.minPickup, { count: values.minDaysPickup })]),
    ...(values.minDaysThrough === null
      ? []
      : [format(words.minThrough, { count: values.minDaysThrough })]),
    ...(values.stopSale ? [words.stopSale] : []),
  ].join(' · ')
}

/**
 * A cell's accessible name: the whole cell in words (W2-S S1): its category,
 * its date, the price or none, the minimums set, stop sale, and the free
 * vehicles of the total, or why they are not known. A day past says so.
 */
export function cellName({
  locale,
  words,
  category,
  day,
  values,
  occupancy,
  boundarySet,
  past,
}: {
  locale: StaffLocale
  words: Words
  category: string
  day: string
  values: DayValues
  occupancy: Occupancy
  boundarySet: boolean
  past: boolean
}): string {
  const details = [
    ...valuesSaid(locale, words, values),
    freeWords(words, occupancy, boundarySet).said,
  ].join('; ')
  return format(past ? words.pastCell : words.cell, {
    category,
    date: formatDate(locale, day),
    details,
  })
}

const isIntOrNull = (value: unknown): value is number | null =>
  value === null || (typeof value === 'number' && Number.isInteger(value))

/**
 * The cell a 409 `version_conflict` carries in `details.cell`: the day as it
 * is now, with the version to send over it; null when the day has no cell
 * any more. Undefined when the answer holds no such thing.
 */
export function conflictCell(
  details: Readonly<Record<string, unknown>>,
): (DayValues & { version: number | null }) | null | undefined {
  const cell = details.cell
  if (cell === null) return null
  if (typeof cell !== 'object' || cell === undefined) return undefined
  const read = cell as Readonly<Record<string, unknown>>
  const { priceCents, minDaysPickup, minDaysThrough, stopSale, version } = read
  if (
    !isIntOrNull(priceCents) ||
    !isIntOrNull(minDaysPickup) ||
    !isIntOrNull(minDaysThrough) ||
    typeof stopSale !== 'boolean' ||
    !isIntOrNull(version)
  ) {
    return undefined
  }
  return { priceCents, minDaysPickup, minDaysThrough, stopSale, version }
}
